import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import cx from 'classnames'
import { v4 as uuidv4 } from 'uuid'

import { useStandOperation } from '@/ble/react/useStandOperation'
import { decideOperationDispatch } from '@/ble/usecases/operationDispatch'
import {
  shouldAutoCloseProcess,
  shouldAutoCloseOutcome,
} from '@/ble/usecases/autoClosePolicy'
import { ScanTimeoutException } from '@/ble/errors/scanTimeout'
import { TransportTimeoutException } from '@/ble/errors/transportTimeout'
import { ResultWaitTimeoutException } from '@/ble/errors/resultWaitTimeout'
import { TagId } from '@/ble/entities/tagId'
import { allowsManualComplete } from '@/ble/entities/standOperationState'

import { useT } from '@/utils/language'
import { useIkasaId } from '@/utils/setting'
import { useRequest } from '@/utils/request'
import { useConfirm } from '@/utils/confirm'
import { ShareRequestPayload } from '@/utils/kasa'
import { Action } from '@/utils/action'
import { closeShareProcessBestEffort } from '@/utils/shareProcess'

import Page from '@/components/Page'
import Icon from '@/components/Icon'

import UnlockGuideError from './UnlockGuideError'
import UnlockError from './UnlockError'
import UnlockCompleteUI, { CompleteUI } from './UnlockCompleteUI'

import RentalHintImage from './images/rental-hint.png'
import ReturnHintImage from './images/return-hint.png'

import styles from './UnlockPage.module.css'

enum UnlockStep {
  GuideError = -4,
  ReadError = -3,
  TimeoutError = -2,
  UnknownError = -1,
  Starting = 0,
  Processing = 1,
  Unlocking = 2,
  Taking = 3,
  Complete = 4,
}

type UnlockStepItemProps = {
  index: number
  success: boolean
  name: string
  hint: string
  showHint: boolean
  showHintLine?: boolean
}

function UnlockStepItem(props: UnlockStepItemProps) {
  const { index, name, success, hint, showHint, showHintLine = true } = props
  return (
    <div className={styles.item}>
      <div className={styles.itemHeader}>
        <div
          className={cx(styles.itemRound, {
            [styles.itemRoundSuccess]: success,
          })}
        >
          {success ? <Icon name="check" size={20} /> : index}
        </div>
        <div className={styles.itemName}>{name}</div>
      </div>
      <div className={styles.itemBody}>
        <div className={styles.itemHintContainer}>
          {showHintLine && <div className={styles.itemHintLine}></div>}
        </div>
        {showHint && <div className={styles.itemHint}>{hint}</div>}
      </div>
    </div>
  )
}

type RentalActionResponse = {
  ikasaId: string
  ksid: number
  rentedScd: number
  pin: number
  transactionId: string
}

type ReturnActionResponse = {
  status: 'OK'
  ksid: number
  scd: number
}

function UnlockPage() {
  const T = useT()
  const { standId } = useParams<{ standId: string }>()
  const [searchParams] = useSearchParams()
  const ikasaId = useIkasaId()
  const request = useRequest()
  const confirm = useConfirm()
  const action = searchParams.get('action') as Action | null

  const navigate = useNavigate()
  const navigateToHome = useCallback(() => {
    navigate('/', { replace: true })
  }, [navigate])

  const [step, setStep] = useState<UnlockStep>(UnlockStep.Starting)
  const [completeUI, setCompleteUI] = useState<CompleteUI | null>(null)

  // 中断（ブラウザバック/クローズ）でアンマウントされた後、await 解決で走る
  // 末尾の setStep/setCompleteUI がアンマウント後の setState にならないためのガード。
  const mountedRef = useRef(true)
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const { execute, phase, operationState, manualComplete } = useStandOperation()

  // phase → UnlockStep（進捗UIの最小マッピング）。terminal(step<0 / Complete)には触れない。
  useEffect(() => {
    setStep((prev) => {
      if (prev < 0 || prev === UnlockStep.Complete) return prev // error/timeout/complete は保持
      switch (phase.type) {
        case 'scanning':
          return UnlockStep.Processing
        case 'establishing':
          return UnlockStep.Unlocking
        case 'ready':
          // 'ready'は接続確立直後（battery読取+wrote POST+解錠writeより前）に
          // emitされるため、ここではTakingにしない。Takingは'operating'（write直前）まで待つ。
          return UnlockStep.Unlocking
        case 'operating':
          return UnlockStep.Taking
        default:
          return prev
      }
    })
  }, [phase])

  const runApiFlow = useCallback(
    async (args: {
      route: 'complete' | 'confirm'
      action: Action
      processId: string
      ikasaId: string
      standId: string
      readTimes: number
      standBattery: number | null
      tagId: string
    }) => {
      const { route, action } = args
      let { processId } = args // confirm 経路で backend の processId に上書きする
      const { ikasaId, standId, readTimes, standBattery, tagId } = args
      let completeUIResponse: CompleteUI | null = null
      // TODO: maybe chargespot
      const platform = ''
      let ksid: number | undefined

      // maybe unlocked
      if (route === 'confirm') {
        const { rental: lastRental, return: lastReturn } =
          await request<ShareRequestPayload>(
            `/service/v2/share/request/ikasa_id/${ikasaId}`
          )
        // using backend processId
        if (action === 'rental' && lastRental?.processId) {
          processId = lastRental.processId
        }
        if (action === 'return' && lastReturn?.processId) {
          processId = lastReturn.processId
        }

        const showConfirm =
          (action === 'rental' && lastRental) ||
          (action === 'return' && lastReturn)

        if (showConfirm) {
          const title =
            action === 'rental'
              ? T('rentalSuccessCheck')
              : T('returnSuccessCheck')

          const confirmResult = await confirm({
            title,
            cancel: T('failed'),
            confirm: T('succeed'),
          }).then(
            () => true,
            () => false
          )

          if (!confirmResult) {
            await request(`/service/v2/share/request/process_id/${processId}`, {
              method: 'DELETE',
            })
            if (mountedRef.current) setStep(UnlockStep.GuideError)
            return
          }
        }

        // read times 1 の場合 ksid は confirm 後の request で確定する
        ksid = action === 'rental' ? lastRental?.ksid : lastReturn?.ksid
      }

      // rental record request
      // rental complete ui
      if (action === 'rental') {
        let rentedScd: number | undefined
        let transactionId: string | undefined

        if (route === 'confirm') {
          const rentalActionResponse = await request<RentalActionResponse>(
            `/service/v2/share/request/rental`,
            {
              method: 'POST',
              data: { ikasaId, ksid, processId },
            }
          )
          // ksid was set before
          rentedScd = rentalActionResponse.rentedScd
          transactionId = rentalActionResponse.transactionId
        } else {
          const rentalActionResponse = await request<RentalActionResponse>(
            `/service/v2/rental`,
            {
              method: 'POST',
              data: { ikasaId, readTimes, standBattery, standId, tagId },
            }
          )
          ksid = rentalActionResponse.ksid
          rentedScd = rentalActionResponse.rentedScd
          transactionId = rentalActionResponse.transactionId
        }

        completeUIResponse = await request<CompleteUI>(
          `/service/v1/mypage/rental`,
          {
            method: 'POST',
            data: { ikasaId, platform, ksid, rentedScd, transactionId },
          }
        )
      }

      // return complete ui
      if (action === 'return') {
        let returnedScd: number | undefined

        if (route === 'confirm' && ksid !== undefined) {
          const returnActionResponse = await request<ReturnActionResponse>(
            `/service/v2/share/request/return`,
            {
              method: 'POST',
              data: { ikasaId, ksid, processId },
            }
          )
          // ksid was set before
          returnedScd = returnActionResponse.scd
        } else if (route === 'complete') {
          const returnActionResponse = await request<ReturnActionResponse>(
            `/service/v2/return`,
            {
              method: 'POST',
              data: { ikasaId, readTimes, standBattery, standId, tagId },
            }
          )
          ksid = returnActionResponse.ksid
          returnedScd = returnActionResponse.scd
        }

        completeUIResponse = await request<CompleteUI>(
          `/service/v1/mypage/return`,
          {
            method: 'POST',
            data: { ikasaId, platform, ksid, returnedScd },
          }
        )
      }

      if (mountedRef.current) {
        setStep(UnlockStep.Complete)
        setCompleteUI(completeUIResponse)
      }
    },
    [T, request, confirm]
  )

  const startUnlock = useCallback(async () => {
    if (!standId || !action) return
    if (!ikasaId) {
      setStep(UnlockStep.UnknownError)
      return
    }
    const processId = uuidv4()
    setStep(UnlockStep.Processing)
    try {
      const exec = await execute({ action, standId, ikasaId, processId })
      if (exec.kind === 'cancelled') return // 中断: 画面は何もしない
      if (exec.kind === 'manualCompleted') {
        // ユーザー申告の完了。読取なし・タグ未登録の固定値で既存 complete route に合流する
        // (dispatch はハード応答の振り分けなので通さない)。
        await runApiFlow({
          route: 'complete',
          action,
          processId,
          ikasaId,
          standId,
          readTimes: 0,
          standBattery: null,
          tagId: TagId.irregular,
        })
        return
      }
      const res = exec.result
      const route = decideOperationDispatch({
        action,
        outcome: res.outcome,
        switchStatus: res.switchStatus,
        forcedUnlock: res.forcedUnlock,
        area1TagId: res.area1TagId,
        area2TagId: res.area2TagId,
      })
      const readTimes = res.readTimes
      const standBattery = res.standBattery
      const tagId = res.tagId?.id ?? TagId.irregular

      if (route === 'error') {
        // 確定失敗(T7: failed×スイッチ非押下の肯定的観測)のみ台帳を即クローズする(#7709)。
        // 判定は正典表の写像 shouldAutoCloseOutcome に集約。'unknown'(想定外バイト)は
        // 解析不能=結果不明として、irregular(T8)と同じく網(来訪時確認)に残す。
        if (shouldAutoCloseOutcome(res)) {
          closeShareProcessBestEffort(request, processId, 'readFailed')
        }
        if (mountedRef.current) setStep(UnlockStep.ReadError)
        return
      }

      // ここから既存の API flow を route で使い分ける。
      // 'complete' → 直接 POST /service/v2/{rental|return}（現行 readTimes===2 経路の一般化）
      // 'confirm'  → 既存 share/request 確認フロー（現行 readTimes===1 経路）
      await runApiFlow({
        route,
        action,
        processId,
        ikasaId,
        standId,
        readTimes,
        standBattery,
        tagId,
      })
    } catch (e: any) {
      // 失敗確定の終端(T11: 後開錠TO×リンク健在)のみ台帳を即クローズ(#7709)。
      // 判定は正典表の写像 shouldAutoCloseProcess に集約。それ以外の終端は従来どおり網に残る。
      if (shouldAutoCloseProcess(e)) {
        closeShareProcessBestEffort(request, processId, 'resultTimeout')
      }
      // scan(接続前)の timeout だけが「接続できなかった」。接続確立後の無応答は、
      // 電池読取(AES 0x08)・書込 ack/result(V14)・結果待ち backstop(AES 沈黙)の
      // どの局面でも「接続済みで読み取れなかった」状態なので読み取り失敗系に分類する
      // (native も standTimeout を読み取り失敗文言で出し、局面を区別しない)。
      // それ以外(write失敗・response stream error・BT off・runApiFlow の HTTP エラー)は
      // UnknownError(現行 unlockDevice().catch と同じ)。
      // （TransportTimeoutException / name==='TimeoutError' の旧チェックは、wrote POST 前の
      // 非正規化経路の防御として維持する。実経路の結果待ちタイムアウトは standService が
      // ResultWaitTimeoutException に正規化する。）
      const isScanTimeout = e instanceof ScanTimeoutException
      const isResultTimeout =
        e instanceof ResultWaitTimeoutException ||
        e instanceof TransportTimeoutException ||
        e?.name === 'TimeoutError'
      if (mountedRef.current) {
        setStep(
          isScanTimeout
            ? UnlockStep.TimeoutError
            : isResultTimeout
            ? UnlockStep.ReadError
            : UnlockStep.UnknownError
        )
      }
    }
  }, [standId, action, ikasaId, execute, runApiFlow, request])

  const isUnlockingStep = useMemo(
    () =>
      [
        UnlockStep.Starting,
        UnlockStep.Processing,
        UnlockStep.Unlocking,
      ].includes(step),
    [step]
  )

  // UnlockStep.Starting: 操作を1回だけ起動する。step===Starting のときだけ発火し、startUnlock が
  // 即 Processing へ遷移させるため二重起動しない（retry は step を Starting へ戻して再発火）。
  // startUnlock を deps に含めないのは意図的で、含めると Starting 中の再 render で二重 execute しうる。
  // 前提: execute の識別は安定（useStandOperation で useCallback 済）／ikasaId は inited gate 通過後に
  // 確定。この不変条件が崩れないことを前提とする。
  useEffect(() => {
    if (step !== UnlockStep.Starting) return
    startUnlock()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const unlockErrorType = useMemo(() => {
    if (step === UnlockStep.ReadError) return 'read'
    if (step === UnlockStep.TimeoutError) return 'timeout'
    if (step === UnlockStep.UnknownError) return 'unknown'
    return ''
  }, [step])

  const showCloseButton = useMemo(() => {
    if (step === UnlockStep.Taking) return false
    return true
  }, [step])

  if (!standId || !action) return null

  return (
    <Page>
      <div
        className={cx(styles.background, {
          [styles.backgroundWhite]: step === UnlockStep.GuideError,
        })}
      >
        {showCloseButton && (
          <button
            className={cx(styles.closeButton, {
              [styles.closeButtonWhite]: step === UnlockStep.GuideError,
            })}
            onClick={navigateToHome}
          >
            <Icon name="close" />
          </button>
        )}

        {/* guide error UI is very difference */}
        {step === UnlockStep.GuideError && (
          <UnlockGuideError
            action={action}
            onRetry={() => {
              setStep(UnlockStep.Starting)
            }}
          />
        )}

        {unlockErrorType && (
          <UnlockError
            type={unlockErrorType}
            onRetry={() => {
              setStep(UnlockStep.Starting)
            }}
          />
        )}

        {step === UnlockStep.Taking && (
          <div className={styles.takingContainer}>
            <div
              className={styles.takingHint}
              dangerouslySetInnerHTML={{ __html: T('unlockTakeHint') }}
            />
            <img
              src={action === 'rental' ? RentalHintImage : ReturnHintImage}
              alt=""
            />
            {allowsManualComplete(operationState) && (
              <button
                className={styles.manualCompleteButton}
                onClick={manualComplete}
              >
                {T(
                  action === 'rental'
                    ? 'manualCompleteRental'
                    : 'manualCompleteReturn'
                )}
              </button>
            )}
          </div>
        )}

        {isUnlockingStep && (
          <div className={styles.container}>
            <div className={styles.title}>{T('pleaseWait')}</div>
            <UnlockStepItem
              index={1}
              success={step >= UnlockStep.Starting}
              name={T('connectStart')}
              hint={T('connectStartHint')}
              showHint={step === UnlockStep.Starting}
            />
            <UnlockStepItem
              index={2}
              success={step >= UnlockStep.Processing}
              name={T('processing')}
              hint={T('processingHint')}
              showHint={step === UnlockStep.Processing}
            />
            <UnlockStepItem
              index={3}
              success={step >= UnlockStep.Unlocking}
              name={T('unlock')}
              hint={T('unlockHint')}
              showHint={step === UnlockStep.Unlocking}
              showHintLine={false}
            />
            <button className={styles.cancelButton} onClick={navigateToHome}>
              {T('cancel')}
            </button>
          </div>
        )}

        {step === UnlockStep.Complete && completeUI !== null && (
          <UnlockCompleteUI
            action={action}
            ui={completeUI}
            onClose={navigateToHome}
          />
        )}
      </div>
    </Page>
  )
}

export default UnlockPage
